import { TNote } from '@ws/shared/types';
import { useContext, useEffect, useRef, useState } from 'react';

import { EditabilityContext } from '../../context/EditabilityContext';

import { SaveManagerExtension } from './SaveManagerExtension';
import { TipTapNoteSplitterExtension } from './NoteSplitterExtension/TipTapNoteSplitterExtension';
import { TipTapEditor } from './TipTapEditor';

export interface IInCompilerEditorProps {
  note: TNote;
}

export function InCompilerEditor({ note }: IInCompilerEditorProps) {
  const noteRef = useRef<TNote>(note);
  const editorRef = useRef<HTMLDivElement | null>(null);
  const { updateNoteContent } = useContext(EditabilityContext);
  const [noteId, setNoteId] = useState(note.id);
  const [content, setContent] = useState(note.content);

  useEffect(() => {
    if (noteRef.current.id !== note.id) {
      noteRef.current = note;

      setNoteId(note.id);
      setContent(note.content);
    }
  }, [note]);

  // contentToSave приходит из SaveManagerExtension (latestContentRef) — рендер-стейт content
  // здесь использовать нельзя: до коммита React он отстаёт от редактора, а debounce-таймер
  // и blur читают ref синхронно (F19; в ленте фикс не был применён, правки терялись).
  // Ветка targetNoteId !== noteRef.current.id в ленте практически недостижима:
  // CollectionCompiler рендерит CompilerContainer с key={entity.id}, NoteCompiler держит одну
  // заметку — оставлена ради единого с WriteBoardEditor контракта.
  // note приходит из useNote → getDeepCopy на каждом рендере, поэтому guard сравнивает с
  // актуальным стором; onSaveRef в SaveManagerExtension гарантирует вызов handleSave
  // последнего коммита.
  function handleSave(contentToSave: string, targetNoteId: string) {
    if (targetNoteId !== noteRef.current.id) {
      updateNoteContent(targetNoteId, contentToSave);
      return;
    }

    if (note.content !== contentToSave) {
      updateNoteContent(targetNoteId, contentToSave);
    }
  }

  return (
    <SaveManagerExtension noteId={noteId} value={content} onChange={setContent} onSave={handleSave}>
      {(saverParams) => (
        <TipTapNoteSplitterExtension note={note} onChange={saverParams.onChange}>
          {(noteSplitterParams) => (
            <TipTapEditor
              ref={editorRef}
              noteId={saverParams.noteId}
              note={saverParams.value}
              onBlur={saverParams.onBlur}
              onChange={saverParams.onChange}
              onCutUpperPart={noteSplitterParams.onSplitNoteAbove}
              onCutLowerPart={noteSplitterParams.onSplitNoteBelow}
            />
          )}
        </TipTapNoteSplitterExtension>
      )}
    </SaveManagerExtension>
  );
}
